import { toast } from "sonner";
import { Eye, RotateCw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatApiError, formatDate, openPayslip } from "@/lib/api";

const METHOD = { codice_fiscale: "per codice fiscale", nome: "per nome", manuale: "assegnato a mano" };

const AssignSelect = ({ batch, item, employees, onChange }) => {
  const value = item.employee_id || "auto";
  const update = async (v) => {
    try {
      const body = v === "auto" ? { clear_employee: true } : { employee_id: v };
      onChange((await api.patch(`/batches/${batch.id}/items/${item.index}`, body)).data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Select value={value} onValueChange={update}>
      <SelectTrigger className="h-8 w-44 text-xs" data-testid={`assign-employee-select-${item.index}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="auto">Automatico</SelectItem>
        {employees.map((e) => (
          <SelectItem key={e.id} value={e.id}>{e.cognome} {e.nome}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const Row = ({ batch, item, employees, onChange, onSendOne, sending }) => {
  const toggle = async (checked) => {
    try {
      onChange((await api.patch(`/batches/${batch.id}/items/${item.index}`, { excluded: !checked })).data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const preview = () => openPayslip(batch.id, item.index).catch((e) => toast.error(formatApiError(e)));
  const canSend = item.match_status === "abbinato";
  return (
    <TableRow data-testid={`payslip-row-${item.index}`} className="hover:bg-slate-50">
      <TableCell>
        <Checkbox checked={item.selected} disabled={!canSend} onCheckedChange={toggle} data-testid={`payslip-include-checkbox-${item.index}`} />
      </TableCell>
      <TableCell className="font-mono text-xs text-slate-500">{item.pages.join(", ")}</TableCell>
      <TableCell>
        <p className="text-sm font-medium text-slate-900">{item.nome_rilevato || "—"}</p>
        <p className="font-mono text-xs text-slate-500">{item.codice_fiscale || "codice fiscale non trovato"}</p>
      </TableCell>
      <TableCell data-testid={`payslip-employee-${item.index}`}>
        {item.employee ? (
          <>
            <p className="text-sm font-medium text-slate-900">{item.employee.cognome} {item.employee.nome}</p>
            <p className="text-xs text-slate-500">{item.employee.email || "nessuna email"} · {METHOD[item.match_method]}</p>
          </>
        ) : <span className="text-sm text-slate-400">Non abbinato</span>}
      </TableCell>
      <TableCell><StatusBadge status={item.match_status} testId={`payslip-match-status-${item.index}`} /></TableCell>
      <TableCell>
        {item.sent_status ? (
          <div className="space-y-1">
            <StatusBadge status={item.sent_status} testId={`payslip-sent-status-${item.index}`} />
            <p className="text-xs text-slate-500">{formatDate(item.sent_at)}</p>
            {item.last_error && <p className="max-w-[220px] text-xs text-red-600">{item.last_error}</p>}
          </div>
        ) : <span className="text-xs text-slate-400">Non inviata</span>}
      </TableCell>
      <TableCell><AssignSelect batch={batch} item={item} employees={employees} onChange={onChange} /></TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" onClick={preview} title="Anteprima" data-testid={`payslip-preview-button-${item.index}`}>
            <Eye className="h-4 w-4" />
          </Button>
          {canSend && onSendOne && (
            <Button size="icon" variant="ghost" disabled={sending} onClick={() => onSendOne(item.index)}
              title={item.sent_status ? "Reinvia" : "Invia solo questa"} data-testid={`payslip-send-one-button-${item.index}`}>
              {item.sent_status ? <RotateCw className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export const BatchItemsTable = ({ batch, employees, onChange, onSendOne, sending }) => (
  <div className="panel overflow-x-auto" data-testid="payslip-review-table">
    <Table className="min-w-[980px]">
      <TableHeader>
        <TableRow>
          <TableHead className="w-10" />
          <TableHead>Pag.</TableHead>
          <TableHead>Rilevato nel PDF</TableHead>
          <TableHead>Dipendente</TableHead>
          <TableHead>Abbinamento</TableHead>
          <TableHead>Invio</TableHead>
          <TableHead>Assegna</TableHead>
          <TableHead className="text-right">Azioni</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {batch.items.map((item) => (
          <Row key={item.index} batch={batch} item={item} employees={employees} onChange={onChange} onSendOne={onSendOne} sending={sending} />
        ))}
      </TableBody>
    </Table>
  </div>
);
